import {useState} from 'react'
import {StripeProductDto} from 'rest/models/StripeProductDto'
import {twMerge} from 'tailwind-merge'

type Props = {
  product: StripeProductDto
  changeSubscriptionStatus: () => void
  unsubscribing: boolean
  subscription: Record<string, any>
}

const getStatusBg = (status: string) => {
  switch (status) {
    case 'active':
      return 'bg-green-50'
    case 'incomplete':
    case 'past_due':
    case 'unpaid':
      return 'bg-yellow-50'
    case 'canceled':
    default:
      return 'bg-red-50'
  }
}

export default function SubscriptionCard({
  product,
  changeSubscriptionStatus,
  unsubscribing,
  subscription,
}: Props) {
  const [showConfirm, setShowConfirm] = useState(false)

  if (!product) return null

  const status = subscription?.status
  const startDate = new Date(subscription.start_date * 1000)
  const endDate = new Date(startDate)

  endDate.setMonth(endDate.getMonth() + 1)

  const endsAt = endDate.toLocaleDateString('en-US')

  const handleAction = () => {
    if (status === 'canceled') {
      changeSubscriptionStatus()
    } else {
      setShowConfirm(true)
    }
  }

  const confirmCancel = () => {
    setShowConfirm(false)
    changeSubscriptionStatus()
  }

  const canCancel = status === 'active'

  return (
    <div
      className={twMerge(
        'max-w-md mt-10 p-6 rounded-2xl shadow-md border transition',
        getStatusBg(status)
      )}>
      <div className="mb-4">
        <h2 className="text-2xl font-bold">{product.name}</h2>
        <div className="flex items-center gap-2 mt-2">
          <span
            className={twMerge(
              'h-2 w-2 rounded-full animate-pulse',
              status === 'canceled'
                ? 'bg-red-600'
                : status === 'active'
                ? 'bg-green-500'
                : 'bg-yellow-500'
            )}
          />
          <span
            className={twMerge(
              'font-medium',
              status === 'canceled'
                ? 'text-red-600'
                : status === 'active'
                ? 'text-green-600'
                : 'text-yellow-600'
            )}>
            {status === 'canceled' ? 'Cancelled' : status}
          </span>
        </div>
      </div>

      <div className="text-gray-700 mb-6">
        <p>
          You are subscribed to the <strong>{product?.name}</strong> with all
          features.
        </p>
        {status === 'active' && (
          <p className="mt-2 text-sm text-gray-500">
            Next billing date: <span className="font-semibold">{endsAt}</span>.
          </p>
        )}

        {status === 'canceled' && (
          <p className="mt-2 text-sm text-gray-500">
            Cancelled on:{' '}
            <span className="font-semibold">
              {new Date(subscription.canceled_at * 1000).toLocaleDateString(
                'en-US'
              )}
            </span>
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={handleAction}
        className={twMerge(
          'w-full font-semibold py-2 px-4 rounded-lg transition',
          status === 'canceled'
            ? 'bg-green-600 hover:bg-green-700 text-white'
            : 'bg-red-600 hover:bg-red-700 text-white',
          canCancel && 'opacity-50'
        )}>
        {unsubscribing
          ? 'Please wait...'
          : status === 'canceled'
          ? 'Reactivate Subscription'
          : 'Cancel Subscription'}
      </button>
      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-4">Cancel Subscription?</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to cancel your subscription to{' '}
              <strong>{product.name}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-md border text-gray-700"
                onClick={() => setShowConfirm(false)}>
                No, go back
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md"
                onClick={confirmCancel}>
                Yes, cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
